/* ============================================================================
   Poter card + slider runtime — isolated from theme JS.

   Exposes: window.PoterCard.init() and window.PoterSlider.init()
   Auto-initializes on DOMContentLoaded and on Shopify section reload events
   (theme-editor live re-render).

   Behaviors:
   - Slider: drag (pointer events), snap-on-release, arrows, edge-fade classes
   - Swatch hover: swaps the card's main <img> src to the variant image
   - Card CTA: "Se Produkt" link → product page (no quick-add; size is chosen on the PDP)
   ============================================================================ */
(function () {
  'use strict';

  // Execute-once guard. This file is included from theme.liquid's <head>
  // (always current) AND from each card-rendering section (may be a stale
  // cached render). Browsers execute every <script> element, even with the
  // same src — without this guard the document-level listeners attach once
  // per copy and quick-add would POST to /cart/add.js multiple times.
  if (window.__poterCardBooted) return;
  window.__poterCardBooted = true;

  // ───────── Swatch hover: active ring + image swap + retarget links ─────────
  var CAN_HOVER = !(window.matchMedia && window.matchMedia('(hover: none)').matches);
  function onSwatchEnter(e) {
    // Hover-only preview. On touch devices a "mouseover" fires on tap, which
    // used to swap the image and retarget URLs mid-tap — confusing on mobile.
    // There, a swatch tap just navigates (handled in onCardClick).
    if (!CAN_HOVER) return;
    var link = e.target.closest('.poter-card__swatch-link');
    if (!link) return;

    var card = link.closest('[data-poter-card]');
    if (!card) return;

    // Move the active ring to this swatch (and keep it there on leave).
    var swatch = link.closest('.poter-card__swatch');
    if (swatch && swatch.parentElement) {
      var siblings = swatch.parentElement.querySelectorAll('.poter-card__swatch');
      Array.prototype.forEach.call(siblings, function (s) {
        if (s === swatch) s.classList.add('poter-card__swatch--active');
        else s.classList.remove('poter-card__swatch--active');
      });
    }

    // Retarget every path that opens the product so the picked colour is where
    // you land — the whole-card click (onCardClick reads data-product-url), the
    // image link, AND the "Se Produkt" button. All three now match a swatch click.
    var variantUrl = link.getAttribute('href');
    if (variantUrl) {
      card.setAttribute('data-product-url', variantUrl);
      var figLink = card.querySelector('.poter-card__fig-link');
      if (figLink) figLink.setAttribute('href', variantUrl);
      var cta = card.querySelector('.poter-card__cta');
      if (cta && cta.tagName === 'A') cta.setAttribute('href', variantUrl);
    }

    // Swap the main image to this variant's image (stays after leave).
    var imgUrl = link.getAttribute('data-swatch-img');
    if (!imgUrl) return;
    var img = card.querySelector('.poter-card__img');
    if (!img) return;
    img.setAttribute('src', imgUrl);
    img.removeAttribute('srcset');
  }

  // ───────── Dual-color swatch SVG refinement ─────────
  // Variant-level swatches (Halsbånd) often arrive as a linear/conic gradient
  // string in --color. Gradient + border-radius edge AA produces visible notches
  // at small sizes. We rebuild them as inline SVG (data URI), which renders the
  // circle + diagonal cut in vector space — pixel-clean at any zoom.
  function buildDualSvgDataUri(c1, c2) {
    var s1 = c1.replace(/#/g, '%23');
    var s2 = c2.replace(/#/g, '%23');
    return "data:image/svg+xml;utf8," +
      "<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 22 22'>" +
        "<circle cx='11' cy='11' r='11' fill='" + s1 + "'/>" +
        "<path d='M22 0L22 22L0 22Z' fill='" + s2 + "'/>" +
      "</svg>";
  }
  function refineDualColorSwatches(root) {
    var links = (root || document).querySelectorAll('.poter-card__swatch-link');
    Array.prototype.forEach.call(links, function (link) {
      if (link.getAttribute('data-poter-svg-applied') === '1') return;
      var color = link.style.getPropertyValue('--color') ||
                  getComputedStyle(link).getPropertyValue('--color').trim();
      if (!color) return;
      if (color.indexOf('url(') >= 0) return;       // PNG or SVG already
      if (color.indexOf('gradient') < 0) return;    // Solid color — no refinement needed
      var hexes = color.match(/#[0-9a-fA-F]{3,8}\b/g);
      if (!hexes || hexes.length < 2) return;
      var dataUri = buildDualSvgDataUri(hexes[0], hexes[1]);
      link.style.setProperty('--color', 'url("' + dataUri + '")');
      link.setAttribute('data-poter-svg-applied', '1');
    });
  }

  // ───────── Recently-viewed mode (Section Rendering API) ─────────
  // Section sets <div class="poter-slider__track" data-poter-recently> with no
  // children server-side. We read localStorage.recentlyViewedProduct (key
  // written by the stock theme's custom-async.js:4219), fetch each product's
  // Poter card via SRA, and inject in order.
  function hideSectionIfEmpty(track) {
    if (window.Shopify && window.Shopify.designMode) return;
    var section = track.closest('[id^="shopify-section-"]') || track.closest('.poter-section');
    if (section) section.style.display = 'none';
  }
  function populateRecentlyViewed(root) {
    var tracks = (root || document).querySelectorAll(
      '[data-poter-recently]:not([data-poter-recently-loaded])'
    );
    if (!tracks.length) return;

    var raw = null;
    try { raw = localStorage.getItem('recentlyViewedProduct'); } catch (e) { /* ignored */ }
    var products = null;
    if (raw) {
      try { products = JSON.parse(raw); } catch (e) { /* ignored */ }
    }
    if (!Array.isArray(products) || !products.length) {
      Array.prototype.forEach.call(tracks, function (track) {
        track.setAttribute('data-poter-recently-loaded', 'empty');
        hideSectionIfEmpty(track);
      });
      return;
    }

    // Stock theme writes oldest-first, so reverse for most-recent-first display.
    var ordered = products.slice().reverse();

    Array.prototype.forEach.call(tracks, function (track) {
      track.setAttribute('data-poter-recently-loaded', 'pending');
      var slots = new Array(ordered.length);
      var pending = ordered.length;

      function finishOne() {
        pending--;
        if (pending > 0) return;
        slots.forEach(function (card) { if (card) track.appendChild(card); });
        track.setAttribute('data-poter-recently-loaded', '1');
        // Fragments can arrive with LEGACY card markup (Shopify caches the
        // poter-card-fragment section render), so run the same CTA upgrade
        // boot applies to server-rendered cards.
        normalizeCardCtas(track);
        refineDualColorSwatches(track);
        // Tell the slider to re-measure scrollWidth now that there's content.
        window.dispatchEvent(new Event('resize'));
        if (!track.children.length) hideSectionIfEmpty(track);
      }

      ordered.forEach(function (entry, idx) {
        if (!entry || !entry.productUrl) { finishOne(); return; }
        var url = entry.productUrl.replace(/\/$/, '') + '?section_id=poter-card-fragment';
        fetch(url, { credentials: 'same-origin' })
          .then(function (r) {
            if (!r.ok) throw new Error('fragment ' + r.status);
            return r.text();
          })
          .then(function (html) {
            var card = new DOMParser().parseFromString(html, 'text/html')
              .querySelector('.poter-card');
            if (card) slots[idx] = card;
            finishOne();
          })
          .catch(function (err) {
            if (window.console) console.warn('[poter-card] recently-viewed fetch failed:', err);
            finishOne();
          });
      });
    });
  }

  // ───────── PoterCard public API ─────────
  // ───────── Brand (vendor) filter sync ─────────
  // The custom brand filter (filters-sorting.liquid:556+) ships with an
  // inline <script> that ticks .vendor-cb checkboxes whose value matches
  // ?filter.p.vendor=… in the URL. That script runs once on page load.
  // When processFilters() in custom-async.js AJAX-replaces the section's
  // innerHTML, inline <script> tags don't re-execute, so after a filter
  // click the brand checkboxes appear unchecked even though the URL has
  // them. This handler delegates at the document level (surviving every
  // DOM swap) and re-syncs the checkboxes after each filter change.
  function syncBrandCheckboxesFromUrl() {
    var params;
    try { params = new URLSearchParams(location.search); } catch (e) { return; }
    var active = params.getAll('filter.p.vendor');
    var boxes = document.querySelectorAll('.vendor-cb');
    Array.prototype.forEach.call(boxes, function (cb) {
      cb.checked = active.indexOf(cb.value) !== -1;
    });
  }
  // Vendor checkbox: fully client-side, NO theme AJAX. The server ignores
  // filter.p.vendor anyway (no Search & Discovery config), so the theme's
  // processFilters() round-trip only produced a jarring full-section blink
  // before our client-side hide re-applied. This capture-phase listener
  // runs BEFORE the theme's element-level change handler and stops the
  // event from ever reaching it: we update the URL ourselves and toggle
  // card visibility instantly.
  function onVendorChangeCapture(e) {
    if (!e.target || !e.target.matches || !e.target.matches('.vendor-cb')) return;
    e.stopPropagation();
    writeVendorParamsToUrl();
    applyVendorFilter(document);
    syncVendorChips();
  }

  // Rewrite ?filter.p.vendor=… in the URL from the currently checked
  // .vendor-cb boxes (replaceState — no navigation, no theme AJAX).
  function writeVendorParamsToUrl() {
    var params;
    try { params = new URLSearchParams(location.search); } catch (err) { return; }
    params.delete('filter.p.vendor');
    var checked = document.querySelectorAll('.vendor-cb:checked');
    Array.prototype.forEach.call(checked, function (cb) {
      params.append('filter.p.vendor', cb.value);
    });
    var qs = params.toString();
    try {
      history.replaceState({}, '', location.pathname + (qs ? '?' + qs : ''));
    } catch (err) { /* ignored */ }
  }

  // ───────── Mærke chips in the active-filters block ─────────
  // Shopify's native active-filters list (filter.active_values) can never
  // include the brand filter — it's our client-side shim, invisible to the
  // server. Mirror the checked vendors into the same chip list so Mærke
  // "registers" exactly like the native availability/price filters, and
  // toggle the block's visibility based on whether ANY chip exists.
  function syncVendorChips() {
    var wrap = document.querySelector('.poter-active-filters-side');
    if (!wrap) return;
    var ul = wrap.querySelector('ul.l4fl');
    if (!ul) return;
    Array.prototype.forEach.call(ul.querySelectorAll('li[data-vendor-chip]'), function (li) {
      li.parentNode.removeChild(li);
    });
    var params;
    try { params = new URLSearchParams(location.search); } catch (e) { return; }
    var active = params.getAll('filter.p.vendor');
    var removeAllA = ul.querySelector('a.remove-all');
    var removeAllLi = removeAllA ? removeAllA.closest('li') : null;
    active.forEach(function (v) {
      var li = document.createElement('li');
      li.setAttribute('data-vendor-chip', v);
      var a = document.createElement('a');
      a.href = '#';
      var span = document.createElement('span');
      span.textContent = 'Mærke';
      a.appendChild(span);
      a.appendChild(document.createTextNode(v));
      li.appendChild(a);
      if (removeAllLi) ul.insertBefore(li, removeAllLi);
      else ul.appendChild(li);
    });
    // Chip li's are classless; the "Dine aktuelle filtre" label li is
    // .strong and the remove-all li is .inline — neither counts.
    var chips = ul.querySelectorAll('li:not(.strong):not(.inline)');
    if (chips.length) wrap.removeAttribute('hidden');
    else wrap.setAttribute('hidden', '');
  }

  function onVendorChipClick(e) {
    if (!e.target || !e.target.closest) return;
    var chipLi = e.target.closest('.poter-active-filters-side li[data-vendor-chip]');
    if (chipLi) {
      e.preventDefault();
      e.stopPropagation();
      var v = chipLi.getAttribute('data-vendor-chip');
      Array.prototype.forEach.call(document.querySelectorAll('.vendor-cb'), function (cb) {
        if (cb.value === v) cb.checked = false;
      });
      writeVendorParamsToUrl();
      applyVendorFilter(document);
      syncVendorChips();
      return;
    }
    var removeAll = e.target.closest('.poter-active-filters-side a.remove-all');
    if (removeAll) {
      // Always clear the client-side brand filter. If native filters are
      // active too, let the link navigate (its href is the clean URL, so
      // the vendor params drop with it); if the brand filter is the ONLY
      // active one, skip the pointless full page reload.
      Array.prototype.forEach.call(document.querySelectorAll('.vendor-cb:checked'), function (cb) {
        cb.checked = false;
      });
      var hasNative = false;
      try {
        new URLSearchParams(location.search).forEach(function (val, key) {
          if (key.indexOf('filter.') === 0 && key !== 'filter.p.vendor') hasNative = true;
        });
      } catch (err) { /* treat as native — full navigation is the safe path */ hasNative = true; }
      if (!hasNative) {
        e.preventDefault();
        writeVendorParamsToUrl();
        applyVendorFilter(document);
        syncVendorChips();
      }
    }
  }

  function runPostSwapPipeline() {
    syncBrandCheckboxesFromUrl();
    applyVendorFilter(document);
    syncVendorChips();
    // Freshly swapped-in cards may carry legacy (cached) CTA markup.
    normalizeCardCtas(document);
    refineDualColorSwatches(document);
  }
  function onFilterFormChange(e) {
    if (!e.target || !e.target.matches) return;
    if (!e.target.matches('#filter input')) return;
    // Immediate pass (fast connections) + a late saftey pass. The real
    // guarantee is the MutationObserver in attachDocListenersOnce, which
    // re-runs the pipeline whenever the grid's DOM is actually replaced —
    // a fixed timeout raced the AJAX and lost on slow responses, which is
    // why filtering appeared broken and legacy orange buttons flashed in.
    setTimeout(runPostSwapPipeline, 150);
    setTimeout(runPostSwapPipeline, 900);
  }

  // ───────── Client-side vendor filtering ─────────
  // Shopify only honors ?filter.p.vendor=… server-side if vendor is enabled
  // as a storefront filter in the Search & Discovery app. On poter-dk-2 it
  // isn't, so the AJAX response returns ALL products regardless of the
  // checked brand. This shim reads the URL, hides .poter-card-grid-item
  // wrappers whose card's data-vendor doesn't match an active brand, and
  // rewrites the "{N} produkter" count text so the UI is consistent.
  // Scope: collection grid only (.l4cl#collection). Sliders untouched.
  function applyVendorFilter(root) {
    root = root || document;
    var grid = root.querySelector('.l4cl#collection');
    if (!grid) return;
    var params;
    try { params = new URLSearchParams(location.search); } catch (e) { return; }
    var active = params.getAll('filter.p.vendor');
    var items = grid.querySelectorAll('.poter-card-grid-item');
    var visibleCount = 0;
    Array.prototype.forEach.call(items, function (li) {
      var card = li.querySelector('.poter-card');
      var v = card ? (card.getAttribute('data-vendor') || '') : '';
      var show = active.length === 0 || active.indexOf(v) !== -1;
      li.style.display = show ? '' : 'none';
      if (show) visibleCount++;
    });
    // Rewrite product-count spans. They're rendered as "{N} produkter" /
    // "{N} produkt"; we replace the leading number while keeping the word.
    var countNodes = document.querySelectorAll(
      '.f8sr .text-start span, .f8sr .has-select span.mobile-only'
    );
    Array.prototype.forEach.call(countNodes, function (span) {
      var t = span.textContent;
      if (!t) return;
      var word = visibleCount === 1 ? 'produkt' : 'produkter';
      span.textContent = visibleCount + ' ' + word;
    });
  }

  // ───────── Card CTA normalization ─────────
  // The card's only action is a plain "Se Produkt" link to the product page —
  // there is NO quick-add on cards, because every product needs a size chosen
  // on the PDP. Cards can still arrive from Shopify's section cache with the
  // OLD markup that included an orange quick-add <button>, so at runtime we
  // simply strip that button and seed the whole-card click target from the CTA
  // link. We deliberately DO NOT change the link's text or href.
  function normalizeCardCtas(root) {
    var cards = (root || document).querySelectorAll('[data-poter-card]');
    Array.prototype.forEach.call(cards, function (card) {
      // Remove any quick-add icon button (old cached markup) — no add-to-cart.
      var quickAdd = card.querySelector('.poter-card__quick-add');
      if (quickAdd && quickAdd.parentNode) quickAdd.parentNode.removeChild(quickAdd);

      var cta = card.querySelector('.poter-card__cta');
      if (!cta || cta.dataset.poterNormalized === '1') return;
      // onCardClick reads data-product-url — seed it from the CTA href so a
      // click anywhere on the card opens the product page.
      if (cta.tagName === 'A') {
        var href = cta.getAttribute('href');
        if (href && !card.getAttribute('data-product-url')) {
          card.setAttribute('data-product-url', href);
        }
      }
      cta.dataset.poterNormalized = '1';
    });
  }

  var PoterCard = {
    init: function (root) {
      root = root || document;
      normalizeCardCtas(root);
      refineDualColorSwatches(root);
      populateRecentlyViewed(root);
      syncBrandCheckboxesFromUrl();
      applyVendorFilter(root);
      syncVendorChips();
    }
  };

  // Whole-card click → navigate to the product's PDP. Skips clicks on any
  // interactive descendant (swatches, cart button, existing links and
  // buttons) so their own handlers keep working. Also skips modified
  // clicks so ctrl/cmd-click still opens a new tab as the user expects.
  function onCardClick(e) {
    if (e.button && e.button !== 0) return;
    if (e.ctrlKey || e.metaKey || e.shiftKey || e.altKey) return;

    var directTarget = e.target;
    var card = directTarget.closest('[data-poter-card]');

    // Overlay rescue: if the tap did NOT land directly on a card, look through
    // everything stacked at the tap point for a card sitting beneath. Some stray
    // transparent layer over the middle of the collection was eating taps, so
    // cards there wouldn't open. elementsFromPoint sees past it to the real card
    // and we open that. Only runs when the direct target isn't a card, so taps
    // on genuine UI (filters, header, etc.) are untouched.
    var viaOverlay = false;
    if (!card && typeof document.elementsFromPoint === 'function' &&
        typeof e.clientX === 'number' && (e.clientX || e.clientY)) {
      var stack = document.elementsFromPoint(e.clientX, e.clientY);
      for (var s = 0; s < stack.length; s++) {
        var el = stack[s];
        if (!el.closest) continue;
        var hit = el.closest('[data-poter-card]');
        if (hit) { card = hit; viaOverlay = true; break; }
        // Walking top-down: if a REAL interactive/navigation element (category
        // menu, filters, a link/button) sits above the card, this tap belongs to
        // THAT — bail so e.g. tapping a category in the left menu opens the
        // category, not a product behind it. Only rescue when the layers on top
        // of the card are inert (a transparent box with no purpose).
        if (el.closest('a, button, input, select, textarea, label, [role="button"], [data-panel], .n6as, .f8fl, #filter, .m6pn, nav')) {
          return;
        }
      }
    }
    if (!card) return;

    // Work out where this tap should go BEFORE anyone else sees the event.
    // A colour swatch (or the "+N" more link) → that variant/product; anything
    // else on the card (image, title, price, empty body, "Se Produkt") → the
    // product, honouring a colour already picked via a swatch. Swatch precision
    // only applies to a direct hit — an overlay-rescued tap opens the product.
    var url = '';
    var swatch = viaOverlay ? null : directTarget.closest('.poter-card__swatch-link, .poter-card__swatch-more');
    if (swatch) {
      url = swatch.getAttribute('href');
    } else {
      url = card.getAttribute('data-product-url');
      if (!url) {
        var cta = card.querySelector('.poter-card__cta');
        if (cta && cta.tagName === 'A') url = cta.getAttribute('href');
      }
      if (!url) {
        var fig = card.querySelector('.poter-card__fig-link');
        url = fig ? fig.getAttribute('href') : '';
      }
    }
    if (!url || url === '#') return;

    // Own the navigation. Running in the CAPTURE phase and stopping the event
    // here means no theme handler (AJAX collection filter / quick-view /
    // in-place variant swap) can intercept the tap and re-render the grid
    // instead of opening the product — which is what made mobile taps flash a
    // white overlay and reshuffle the list without navigating.
    e.preventDefault();
    e.stopPropagation();
    window.location.href = url;
  }

  // ───────── Filter sidebar accordion ─────────
  // Group headings (Kategorier + the filter h4s) collapse/expand their
  // group on click. "Group" = every sibling after the heading up to the
  // next H4 (or FOOTER — the sticky show-products footer must stay).
  // Collapse uses inline style.setProperty(..., 'important') because the
  // sidebar's inline <style> force-shows content with !important rules.
  function onFilterGroupToggle(e) {
    var h = e.target.closest('.m6cl > aside .f8fl h4, .m6cl > aside .n6as-kategorier-heading');
    if (!h) return;
    var collapsed = h.classList.toggle('poter-collapsed');
    var el = h.nextElementSibling;
    while (el && el.tagName !== 'H4' && el.tagName !== 'FOOTER') {
      if (collapsed) el.style.setProperty('display', 'none', 'important');
      else el.style.removeProperty('display');
      el = el.nextElementSibling;
    }
  }

  // Delegated listeners attached once at document level — survive section re-renders
  var _docListenersAttached = false;
  function attachDocListenersOnce() {
    if (_docListenersAttached) return;
    _docListenersAttached = true;
    // Capture phase (3rd arg true) so we decide the navigation before any
    // theme click handler can hijack a card tap.
    document.addEventListener('click', onCardClick, true);
    document.addEventListener('click', onFilterGroupToggle);
    document.addEventListener('click', onVendorChipClick);
    document.addEventListener('mouseover', onSwatchEnter);
    // Capture phase — must beat the theme's element-level filter handler.
    document.addEventListener('change', onVendorChangeCapture, true);
    document.addEventListener('change', onFilterFormChange);
    // Whenever ANY DOM swap inserts poter cards (filter AJAX, cart drawer
    // refresh, SRA loads), re-run the pipeline. Debounced so a burst of
    // mutations costs one pass. This is the timing-proof backstop for
    // legacy-markup upgrades and client-side vendor filtering.
    var moTimer = null;
    new MutationObserver(function (muts) {
      var relevant = false;
      for (var i = 0; i < muts.length; i++) {
        var added = muts[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          var n = added[j];
          if (n.nodeType !== 1) continue;
          if ((n.matches && n.matches('[data-poter-card], .poter-card-grid-item, .l4cl')) ||
              (n.querySelector && n.querySelector('[data-poter-card]'))) {
            relevant = true;
            break;
          }
        }
        if (relevant) break;
      }
      if (!relevant) return;
      clearTimeout(moTimer);
      moTimer = setTimeout(runPostSwapPipeline, 60);
    }).observe(document.body, { childList: true, subtree: true });
  }

  // ───────── Slider ─────────
  var PoterSlider = {
    init: function (rootOrSelector) {
      var root = typeof rootOrSelector === 'string'
        ? document.querySelector(rootOrSelector)
        : rootOrSelector;
      if (!root) return;
      Array.prototype.forEach.call(
        root.querySelectorAll('.poter-slider'),
        initOneSlider
      );
    }
  };

  function initOneSlider(slider) {
    if (slider._poterInitialized) return;
    slider._poterInitialized = true;

    var viewport = slider.querySelector('.poter-slider__viewport');
    var track    = slider.querySelector('.poter-slider__track');
    var prev     = slider.querySelector('.poter-slider__nav--prev');
    var next     = slider.querySelector('.poter-slider__nav--next');
    if (!viewport || !track || !prev || !next) return;

    var CARD_WIDTH = 290;
    var GAP        = 24;
    var STEP       = CARD_WIDTH + GAP;
    var DRAG_THRESHOLD = 5;

    var offset = 0;
    var dragging = false;
    var dragStartX = 0;
    var dragStartY = 0;
    var dragStartOffset = 0;
    var dragDistance = 0;
    var axisLock = null; // 'x' = horizontal slider drag, 'y' = vertical page scroll

    function readCardWidth() {
      var first = track.querySelector('.poter-card');
      if (!first) return CARD_WIDTH;
      var w = first.getBoundingClientRect().width;
      return w > 0 ? w : CARD_WIDTH;
    }

    function maxOffset() {
      var cs = getComputedStyle(viewport);
      var pad = parseFloat(cs.paddingLeft || 0) + parseFloat(cs.paddingRight || 0);
      return Math.max(0, track.scrollWidth - (viewport.clientWidth - pad));
    }

    function apply() {
      track.style.transform = 'translateX(-' + offset + 'px)';
      var max = maxOffset();
      var atStart = offset <= 0;
      var atEnd   = offset >= max - 2;
      prev.disabled = atStart;
      next.disabled = atEnd;
      viewport.classList.toggle('is-at-start', atStart);
      viewport.classList.toggle('is-at-end', atEnd);
    }

    function snap(rawOffset) {
      var max = maxOffset();
      var step = readCardWidth() + GAP;
      var snapped = Math.round(rawOffset / step) * step;
      snapped = Math.max(0, Math.min(max, snapped));
      if (Math.abs(rawOffset - max) < Math.abs(rawOffset - snapped)) {
        return max;
      }
      return snapped;
    }

    prev.addEventListener('click', function () {
      offset = Math.max(0, offset - (readCardWidth() + GAP));
      apply();
    });
    next.addEventListener('click', function () {
      offset = Math.min(maxOffset(), offset + (readCardWidth() + GAP));
      apply();
    });

    var capturedPointerId = null;
    var dragStartTime = 0;
    track.addEventListener('pointerdown', function (e) {
      if (e.button && e.button !== 0) return;
      dragging = true;
      dragStartX = e.clientX;
      dragStartY = e.clientY;
      dragStartOffset = offset;
      dragDistance = 0;
      dragStartTime = Date.now();
      axisLock = null;
      capturedPointerId = null;
      // DO NOT setPointerCapture here — it redirects the synthetic click
      // to the track and the inner <a> never sees it, breaking all card
      // links inside the slider. Capture only after the user actually
      // exceeds DRAG_THRESHOLD (see pointermove below).
    });
    track.addEventListener('pointermove', function (e) {
      if (!dragging) return;
      var dx = e.clientX - dragStartX;
      var dy = e.clientY - dragStartY;
      dragDistance = Math.abs(dx);

      // Axis lock — decide ONCE, on the first meaningful movement, whether this
      // gesture is a horizontal slider drag or a vertical page scroll. If the
      // finger moves mostly vertically the user wants to scroll the page: bail
      // out so we never capture the pointer, and the browser scrolls freely
      // (touch-action: pan-y on the track also permits this). This is what lets
      // you both swipe the slider AND scroll past it on mobile.
      if (axisLock === null) {
        if (Math.abs(dx) > DRAG_THRESHOLD || Math.abs(dy) > DRAG_THRESHOLD) {
          axisLock = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
          if (axisLock === 'y') { dragging = false; return; }
        } else {
          return; // not enough movement yet to tell
        }
      }
      if (axisLock !== 'x') return;

      if (capturedPointerId === null) {
        // Real horizontal drag — capture so we don't lose pointer events if the
        // finger/cursor leaves the track mid-drag.
        try { track.setPointerCapture(e.pointerId); capturedPointerId = e.pointerId; } catch (_) {}
        track.classList.add('is-dragging');
      }
      var raw = dragStartOffset - dx;
      offset = Math.max(0, Math.min(maxOffset(), raw));
      apply();
    });
    // Where the track should settle after a drag. A plain "snap to nearest"
    // meant a short swipe (less than half a card) sprang back, so you had to
    // drag slowly across a whole card for anything to happen. Now: if the drag
    // crossed into another card, snap there (long drags can move several cards);
    // if it stayed on the same card BUT was a deliberate flick (quick, or past
    // ~15% of a card), advance ONE card in the swipe direction. Quick short
    // swipes register instead of snapping back.
    function settleOffset() {
      var step = readCardWidth() + GAP;
      var max = maxOffset();
      var moved = offset - dragStartOffset;                 // + = toward the end
      var dt = Math.max(Date.now() - dragStartTime, 1);
      var velocity = moved / dt;                            // px per ms
      var startIndex = Math.round(dragStartOffset / step);
      var nearestIndex = Math.round(offset / step);
      var target = nearestIndex * step;
      if (nearestIndex === startIndex &&
          (Math.abs(velocity) > 0.25 || Math.abs(moved) > step * 0.15)) {
        target = (moved > 0 ? startIndex + 1 : startIndex - 1) * step;
      }
      return Math.max(0, Math.min(max, target));
    }
    function endDrag(e) {
      if (!dragging) return;
      dragging = false;
      track.classList.remove('is-dragging');
      if (capturedPointerId !== null) {
        try { track.releasePointerCapture(capturedPointerId); } catch (_) {}
        capturedPointerId = null;
      }
      offset = settleOffset();
      apply();
    }
    track.addEventListener('pointerup', endDrag);
    track.addEventListener('pointercancel', endDrag);
    track.addEventListener('pointerleave', function (e) {
      if (dragging && e.buttons === 0) endDrag(e);
    });

    // Suppress click after a real drag (so the link/button click doesn't fire)
    track.addEventListener('click', function (e) {
      if (dragDistance > DRAG_THRESHOLD) {
        e.preventDefault();
        e.stopPropagation();
      }
      dragDistance = 0;
    }, true);

    track.addEventListener('dragstart', function (e) { e.preventDefault(); });

    var resizeTimer;
    window.addEventListener('resize', function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(function () {
        offset = Math.min(offset, maxOffset());
        apply();
      }, 80);
    });

    apply();
  }

  // ───────── Boot ─────────
  function bootAll() {
    attachDocListenersOnce();
    PoterCard.init(document);
    PoterSlider.init(document);
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootAll);
  } else {
    bootAll();
  }
  // Shopify theme-editor live re-render hooks
  document.addEventListener('shopify:section:load', function (e) {
    PoterCard.init(e.target);
    PoterSlider.init(e.target);
  });

  window.PoterCard = PoterCard;
  window.PoterSlider = PoterSlider;
})();
